/**
 * UploadPresetPage - Share a preset to the marketplace
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ChevronLeft,
  Upload,
  Music,
  Sliders,
  Tag,
  Info,
  Check,
  AlertCircle,
  DollarSign,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const UploadPresetPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [presetType, setPresetType] = useState('track');
  const [genre, setGenre] = useState('');
  const [instrumentType, setInstrumentType] = useState('');
  const [style, setStyle] = useState('');
  const [tags, setTags] = useState('');
  const [pluginChain, setPluginChain] = useState([]);
  const [priceTier, setPriceTier] = useState('free');
  const [sharingMode, setSharingMode] = useState('share'); // share, sell, trade
  const [allowRemix, setAllowRemix] = useState(true);
  const [licenseType, setLicenseType] = useState('standard');
  const [tradeWants, setTradeWants] = useState(''); // What user wants in trade
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Sharing modes
  const SHARING_MODES = {
    share: { label: 'Share Free', icon: '🎁', description: 'Give back to the community' },
    sell: { label: 'Sell', icon: '💰', description: 'Earn from your creations' },
    trade: { label: 'Trade', icon: '🔄', description: 'Trade for other presets' }
  };

  // License types
  const LICENSE_TYPES = {
    standard: { label: 'Standard', description: 'Others can use but not resell' },
    exclusive: { label: 'Exclusive', description: 'One-time sale, buyer gets full rights' },
    creative_commons: { label: 'Creative Commons', description: 'Free to use with attribution' }
  };
  
  // Price tiers configuration
  const PRICE_TIERS = {
    free: { label: 'Free', price: 0, description: 'Share freely with the community' },
    basic: { label: 'Basic', price: 2.99, description: 'Simple presets' },
    standard: { label: 'Standard', price: 4.99, description: 'Quality presets' },
    premium: { label: 'Premium', price: 9.99, description: 'Professional presets' },
    professional: { label: 'Professional', price: 19.99, description: 'Studio-grade presets' }
  };
  
  // Categories
  const [categories, setCategories] = useState({ genres: [], instrument_types: [], styles: [] });
  
  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      toast.error('Please log in to share presets');
      navigate('/');
    }
  }, [user, navigate]);
  
  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/api/marketplace/categories`);
        if (response.ok) {
          const data = await response.json();
          setCategories(data);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);
  
  // Check for preset to upload from localStorage
  useEffect(() => {
    const presetToShare = localStorage.getItem('presetToShare');
    if (presetToShare) {
      try {
        const parsed = JSON.parse(presetToShare);
        setPluginChain(parsed.pluginChain || []);
        setPresetType(parsed.presetType || 'track');
        if (parsed.name) setName(parsed.name);
        localStorage.removeItem('presetToShare');
        toast.success('Preset loaded from studio!');
      } catch (e) {
        console.error('Failed to load preset:', e);
      }
    }
  }, []);
  
  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Please enter a preset name');
      return;
    }
    
    if (pluginChain.length === 0) {
      toast.error('Please add at least one plugin to your preset');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const presetData = {
        name: name.trim(),
        description: description.trim(),
        preset_type: presetType,
        plugin_chain: pluginChain.map(p => ({
          id: p.id,
          type: p.type || p.name?.toLowerCase().replace(/\s+/g, '-'),
          name: p.name,
          enabled: p.enabled !== false,
          params: p.params || p.getState?.()?.parameters || {}
        })),
        genre: genre || null,
        instrument_type: instrumentType || null,
        style: style || null,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
        price_tier: priceTier,
        price: PRICE_TIERS[priceTier].price
      };
      
      const response = await fetch(
        `${API_URL}/api/marketplace/presets?user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(presetData)
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'pending') {
          toast.success('Preset submitted for review!');
        } else {
          toast.success('Preset published successfully!');
        }
        navigate('/marketplace');
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload preset');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload preset');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Add sample plugin for demo
  const addSamplePlugin = () => {
    setPluginChain(prev => [
      ...prev,
      {
        id: `plugin-${Date.now()}`,
        type: 'compressor',
        name: 'Compressor',
        enabled: true,
        params: {
          threshold: -20,
          ratio: 4,
          attack: 10,
          release: 100
        }
      }
    ]);
  };
  
  // Remove plugin
  const removePlugin = (index) => {
    setPluginChain(prev => prev.filter((_, i) => i !== index));
  };
  
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="bg-zinc-900/50 border-b border-zinc-800 sticky top-0 z-40 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/marketplace')}
              className="text-zinc-400 hover:text-white"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Upload className="w-5 h-5 text-violet-500" />
                Share Preset
              </h1>
              <p className="text-sm text-zinc-400">Share your plugin preset with the community</p>
            </div>
          </div>
        </div>
      </header>
      
      {/* Form */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
          >
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Info className="w-5 h-5 text-violet-500" />
              Basic Information
            </h2>
            
            {/* Name */}
            <div className="mb-4">
              <label className="text-sm text-zinc-400 mb-2 block">Preset Name *</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Punchy Vocal Chain"
                className="bg-zinc-800/50 border-zinc-700 focus:border-violet-500"
                maxLength={100}
                data-testid="preset-name-input"
              />
            </div>
            
            {/* Description */}
            <div className="mb-4">
              <label className="text-sm text-zinc-400 mb-2 block">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this preset does and when to use it..."
                className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-violet-500 min-h-[100px] resize-none"
                maxLength={1000}
              />
            </div>
            
            {/* Preset Type */}
            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Preset Type *</label>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setPresetType('track')}
                  className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                    presetType === 'track'
                      ? 'border-cyan-500 bg-cyan-500/10'
                      : 'border-zinc-800 bg-zinc-800/50 hover:border-zinc-700'
                  }`}
                >
                  <Music className={`w-8 h-8 mx-auto mb-2 ${presetType === 'track' ? 'text-cyan-500' : 'text-zinc-400'}`} />
                  <p className={`font-medium ${presetType === 'track' ? 'text-white' : 'text-zinc-400'}`}>Track Preset</p>
                  <p className="text-xs text-zinc-500 mt-1">For individual tracks</p>
                </button>
                
                <button
                  type="button"
                  onClick={() => setPresetType('master')}
                  className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                    presetType === 'master'
                      ? 'border-amber-500 bg-amber-500/10'
                      : 'border-zinc-800 bg-zinc-800/50 hover:border-zinc-700'
                  }`}
                >
                  <Sliders className={`w-8 h-8 mx-auto mb-2 ${presetType === 'master' ? 'text-amber-500' : 'text-zinc-400'}`} />
                  <p className={`font-medium ${presetType === 'master' ? 'text-white' : 'text-zinc-400'}`}>Master Preset</p>
                  <p className="text-xs text-zinc-500 mt-1">For master bus/mastering</p>
                </button>
              </div>
            </div>
          </motion.div>
          
          {/* Categories */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
          >
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Tag className="w-5 h-5 text-violet-500" />
              Categories
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Genre */}
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Genre</label>
                <select
                  value={genre}
                  onChange={(e) => setGenre(e.target.value)}
                  className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-violet-500"
                >
                  <option value="">Select Genre</option>
                  {categories.genres?.map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
              
              {/* Instrument Type */}
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Instrument</label>
                <select
                  value={instrumentType}
                  onChange={(e) => setInstrumentType(e.target.value)}
                  className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-violet-500"
                >
                  <option value="">Select Instrument</option>
                  {categories.instrument_types?.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              
              {/* Style */}
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Style</label>
                <select
                  value={style}
                  onChange={(e) => setStyle(e.target.value)}
                  className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-violet-500"
                >
                  <option value="">Select Style</option>
                  {categories.styles?.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Tags */}
            <div className="mt-4">
              <label className="text-sm text-zinc-400 mb-2 block">Tags (comma separated)</label>
              <Input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="e.g., vocal, pop, radio-ready, loud"
                className="bg-zinc-800/50 border-zinc-700 focus:border-violet-500"
              />
            </div>
          </motion.div>
          
          {/* Plugin Chain */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Sliders className="w-5 h-5 text-violet-500" />
                Plugin Chain *
              </h2>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={addSamplePlugin}
                className="text-violet-400 hover:text-violet-300"
              >
                Add Sample Plugin
              </Button>
            </div>
            
            {pluginChain.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-zinc-800 rounded-xl">
                <AlertCircle className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
                <p className="text-zinc-500 mb-2">No plugins in chain</p>
                <p className="text-xs text-zinc-600">
                  Create a preset in the studio and use "Share to Marketplace" button,<br />
                  or add a sample plugin to test.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {pluginChain.map((plugin, index) => (
                  <div
                    key={plugin.id}
                    className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                        <span className="text-violet-400 font-mono text-sm">{index + 1}</span>
                      </div>
                      <div>
                        <p className="font-medium text-white">{plugin.name}</p>
                        <p className="text-xs text-zinc-500">{plugin.type}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removePlugin(index)}
                      className="text-zinc-400 hover:text-red-400 transition-colors"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
          
          {/* Sharing Mode */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.23 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
          >
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Tag className="w-5 h-5 text-orange-500" />
              How do you want to share?
            </h2>
            
            <div className="grid grid-cols-3 gap-3 mb-4">
              {Object.entries(SHARING_MODES).map(([key, mode]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSharingMode(key)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    sharingMode === key
                      ? key === 'share' 
                        ? 'border-emerald-500 bg-emerald-500/10'
                        : key === 'sell'
                        ? 'border-violet-500 bg-violet-500/10'
                        : 'border-orange-500 bg-orange-500/10'
                      : 'border-zinc-800 bg-zinc-800/50 hover:border-zinc-700'
                  }`}
                  data-testid={`sharing-mode-${key}`}
                >
                  <div className="text-2xl mb-2">{mode.icon}</div>
                  <div className={`font-bold mb-1 ${
                    sharingMode === key 
                      ? key === 'share' ? 'text-emerald-400' 
                        : key === 'sell' ? 'text-violet-400' 
                        : 'text-orange-400'
                      : 'text-white'
                  }`}>
                    {mode.label}
                  </div>
                  <div className="text-xs text-zinc-500">{mode.description}</div>
                </button>
              ))}
            </div>

            {/* Trade specific field */}
            {sharingMode === 'trade' && (
              <div className="mt-4 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <label className="text-sm text-orange-400 mb-2 block">What are you looking for in trade?</label>
                <Input
                  value={tradeWants}
                  onChange={(e) => setTradeWants(e.target.value)}
                  placeholder="e.g., Metal amp presets, drum samples, vocal chains..."
                  className="bg-zinc-800/50 border-zinc-700 focus:border-orange-500"
                />
              </div>
            )}

            {/* License & Remix Options */}
            <div className="mt-4 pt-4 border-t border-zinc-800">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-medium">Allow Remixing</p>
                  <p className="text-xs text-zinc-500">Let others modify your preset</p>
                </div>
                <button
                  type="button"
                  onClick={() => setAllowRemix(!allowRemix)}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    allowRemix ? 'bg-emerald-500' : 'bg-zinc-700'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transform transition-transform ${
                    allowRemix ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
              
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">License Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(LICENSE_TYPES).map(([key, license]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setLicenseType(key)}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        licenseType === key
                          ? 'border-violet-500 bg-violet-500/10'
                          : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                      }`}
                    >
                      <div className={`text-sm font-medium ${licenseType === key ? 'text-violet-400' : 'text-white'}`}>
                        {license.label}
                      </div>
                      <div className="text-xs text-zinc-500 mt-0.5">{license.description}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Pricing - Only show for sell mode */}
          {sharingMode === 'sell' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
          >
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-500" />
              Pricing
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(PRICE_TIERS).filter(([key]) => key !== 'free').map(([key, tier]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setPriceTier(key)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    priceTier === key
                      ? 'border-violet-500 bg-violet-500/10'
                      : 'border-zinc-800 bg-zinc-800/50 hover:border-zinc-700'
                  }`}
                  data-testid={`price-tier-${key}`}
                >
                  <div className={`text-lg font-bold mb-1 ${
                    priceTier === key ? 'text-violet-400' : 'text-white'
                  }`}>
                    ${tier.price.toFixed(2)}
                  </div>
                  <div className="text-xs text-zinc-500">{tier.label}</div>
                </button>
              ))}
            </div>
            
            <div className="mt-4 p-3 bg-zinc-800/50 rounded-lg">
              <div className="flex items-center justify-between text-sm">
                <span className="text-zinc-400">Your earnings per sale:</span>
                <span className="text-emerald-400 font-semibold">
                  ${(PRICE_TIERS[priceTier].price * 0.85).toFixed(2)} (85%)
                </span>
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                15% platform fee • Upgrade to Creator Pro for only 10% fee
              </p>
            </div>
          </motion.div>
          )}
          
          {/* Submit */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex items-center justify-between"
          >
            <p className="text-sm text-zinc-500">
              <AlertCircle className="w-4 h-4 inline mr-1" />
              New users' presets are reviewed before publishing
            </p>
            
            <div className="flex gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate('/marketplace')}
                className="text-zinc-400"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !name.trim() || pluginChain.length === 0}
                className="bg-violet-600 hover:bg-violet-500 disabled:opacity-50"
                data-testid="submit-preset-btn"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Share Preset
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </form>
      </main>
    </div>
  );
};

export default UploadPresetPage;
